import { useMemo } from "react";
import { Navigate } from "react-router-dom";
import { BtnLoader } from "src/Loader";
import { BADGE_PADDING_STYLE, PAYMENT_BADGE_CLASS, getPaymentStatusStyle } from "src/constants/orderStatusBadges";
import { useAuth } from "src/contexts/auth-provider";
import { OrderRowDate } from "src/components/order-tables/OrderRowDate";
import { formatCentsToDollarsWithDollarSign } from "src/helpers";
import { ReportPageLayout } from "src/features/Reports/components/ReportPageLayout";
import { useRestaurantFromParams } from "src/utils/useRestaurantFromParams";
import { getPaginationPages, useTransactionsPayments } from "./hooks/useTransactionsPayments";
import type { PaymentNode } from "./types";

const ALL_TRANSACTIONS_START_MS = 0;
const EMPTY_CELL = "-";
const TABLE_HEAD_CLASS =
  "py-3 px-2 align-middle text-left text-[12px] font-semibold uppercase tracking-wide text-[#374151] border-b border-[#e2e8f0] bg-[#f1f5f9] whitespace-nowrap";
const TABLE_CELL_CLASS = "px-2 py-2 align-middle text-sm text-[#111827] whitespace-nowrap";

function parsePaymentDateMs(value: number | string | null | undefined) {
  if (value == null || value === "") return null;

  const numericValue = typeof value === "number" ? value : Number(value);
  const date =
    Number.isFinite(numericValue) && String(value).trim() !== ""
      ? new Date(numericValue < 1_000_000_000_000 ? numericValue * 1000 : numericValue)
      : new Date(value);

  if (Number.isNaN(date.getTime())) return null;
  return date.getTime();
}

function formatOrderReference(payment: PaymentNode) {
  if (!payment.customerOrderCorrelationId) return EMPTY_CELL;
  return `#${payment.customerOrderCorrelationId.slice(-5)}`;
}

function formatPaymentMethod(payment: PaymentNode) {
  const brand = payment.paymentMethodBrand ?? payment.cardType ?? payment.paymentMethodType;
  if (brand && payment.last4Digits) return `${brand} **** ${payment.last4Digits}`;
  const fallback = brand ?? (payment.paymentType === "CARD" ? "Card" : payment.paymentType);
  if (!fallback) return EMPTY_CELL;
  return fallback.replace(/_/g, " ").toLowerCase().replace(/^./, (char) => char.toUpperCase());
}

export function FinanceTransactionsPage() {
  const { hasRestaurant } = useAuth();
  const { redirectTo } = useRestaurantFromParams();
  const storeCorrelationId = hasRestaurant?.correlationId ?? null;
  const nowPlusOneDay = useMemo(() => Date.now() + 24 * 60 * 60 * 1000, []);

  const {
    payments,
    loading,
    error,
    currentPage,
    totalPages,
    totalPayments,
    start,
    end,
    loadNextPage,
    loadPrevPage,
    goToPage,
    canGoToPage,
  } = useTransactionsPayments({
    storeCorrelationId,
    startTimeInEpochMilli: ALL_TRANSACTIONS_START_MS,
    endTimeInEpochMilli: nowPlusOneDay,
    paymentTypeFilter: "CARD",
  });

  if (redirectTo) return <Navigate to={redirectTo} replace />;
  if (!storeCorrelationId) return <ReportPageLayout hideTitle>Select a restaurant to view payments.</ReportPageLayout>;

  const paginationPages = getPaginationPages(currentPage, totalPages);

  return (
    <ReportPageLayout hideTitle fillHeight error={error ?? undefined}>
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-[#F1F1F2] bg-white shadow-[0_4px_12px_rgba(0,0,0,0.04)]">
        {loading && payments.length === 0 ? (
          <div className="flex min-h-[220px] flex-1 items-center justify-center">
            <BtnLoader />
          </div>
        ) : (
          <>
            <div className="min-h-0 flex-1 overflow-auto">
              <table className="w-full min-w-[1040px] border-collapse table-auto text-left">
                <thead>
                  <tr>
                    <th className={`pl-4 pr-2 min-w-[13rem] ${TABLE_HEAD_CLASS}`}>Date</th>
                    <th className={`min-w-[7rem] ${TABLE_HEAD_CLASS}`}>Order</th>
                    <th className={`min-w-[7rem] ${TABLE_HEAD_CLASS}`}>Amount</th>
                    <th className={`min-w-[9rem] ${TABLE_HEAD_CLASS}`}>Status</th>
                    <th className={`min-w-[14rem] ${TABLE_HEAD_CLASS}`}>Payment Method</th>
                    <th className={`min-w-[14rem] ${TABLE_HEAD_CLASS}`}>Transfer ID</th>
                    <th className={`pl-2 pr-4 min-w-[5rem] ${TABLE_HEAD_CLASS}`}>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-10 text-center text-sm text-[#6b7280]">
                        No transactions.
                      </td>
                    </tr>
                  ) : (
                    payments.map((payment) => {
                      const paymentStyle = getPaymentStatusStyle({ paymentStatus: payment.paymentStatus ?? undefined });
                      const dateMs = parsePaymentDateMs(payment.paymentDate ?? payment.createdAt);
                      return (
                        <tr
                          key={payment.correlationId}
                          className="border-t border-[#f1f5f9] text-sm text-[#111827] transition-colors hover:bg-[#F9FAFB]"
                        >
                          <td className={`pl-4 pr-2 ${TABLE_CELL_CLASS}`}>
                            <OrderRowDate createdAt={dateMs} />
                          </td>
                          <td className={`${TABLE_CELL_CLASS} font-mono text-xs text-[#64748b] bg-slate-50/80`}>
                            {formatOrderReference(payment)}
                          </td>
                          <td className={`${TABLE_CELL_CLASS} font-semibold text-[#0f172a] tabular-nums`}>
                            {formatCentsToDollarsWithDollarSign(payment.paymentAmountCents ?? 0)}
                          </td>
                          <td className={TABLE_CELL_CLASS}>
                            <span
                              className={PAYMENT_BADGE_CLASS}
                              style={{
                                ...BADGE_PADDING_STYLE,
                                backgroundColor: paymentStyle.backgroundColor,
                                color: paymentStyle.color,
                              }}
                            >
                              {paymentStyle.label}
                            </span>
                          </td>
                          <td className={`${TABLE_CELL_CLASS} text-gray-600`}>{formatPaymentMethod(payment)}</td>
                          <td className={`${TABLE_CELL_CLASS} font-mono text-xs text-[#64748b]`}>
                            {payment.finixTransferId ?? <span className="text-[#9CA3AF] font-normal">{EMPTY_CELL}</span>}
                          </td>
                          <td className={`pl-2 pr-4 ${TABLE_CELL_CLASS} text-gray-600`}>
                            {payment.paymentType ?? EMPTY_CELL}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            <div
              className="grid shrink-0 gap-2 border-t border-[#F1F1F2] bg-[#fafafa] px-3 py-3.5 text-sm text-[#6b7280] sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] sm:items-center"
              aria-label="Pagination"
            >
              <span className="order-2 min-w-0 sm:order-1 sm:text-left">
                {totalPayments > 0 ? `Showing ${start}-${end} of ${totalPayments}` : "Showing 0 of 0"}
                {loading ? " - Loading" : ""}
              </span>
              <div className="order-1 flex flex-wrap items-center justify-center gap-2 sm:order-2">
                <button
                  type="button"
                  className="flex h-9 w-14 min-w-[56px] items-center justify-center rounded-lg border border-[#F1F1F2] bg-white text-sm font-medium text-[#252525] hover:bg-[#fafafa] disabled:cursor-default disabled:opacity-[0.5]"
                  disabled={currentPage <= 1 || loading}
                  onClick={loadPrevPage}
                >
                  Prev
                </button>
                <div className="flex items-center gap-1">
                  {paginationPages.map((page, index) =>
                    page === "ellipsis" ? (
                      <span key={`ellipsis-${index}`} className="px-2 text-[#9ca3af]">
                        ...
                      </span>
                    ) : (
                      <button
                        key={page}
                        type="button"
                        disabled={loading || !canGoToPage(page)}
                        onClick={() => goToPage(page)}
                        className={`flex h-9 w-9 min-w-[36px] items-center justify-center rounded-lg text-sm font-medium ${
                          page === currentPage
                            ? "bg-orange text-white"
                            : "bg-gray-50 text-gray-700"
                        } disabled:cursor-default disabled:opacity-[0.5]`}
                      >
                        {page}
                      </button>
                    )
                  )}
                </div>
                <button
                  type="button"
                  className="flex h-9 w-14 min-w-[56px] items-center justify-center rounded-lg border border-[#F1F1F2] bg-white text-sm font-medium text-[#252525] hover:bg-[#fafafa] disabled:cursor-default disabled:opacity-[0.5]"
                  disabled={currentPage >= totalPages || loading}
                  onClick={loadNextPage}
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:order-3 sm:block" aria-hidden="true" />
            </div>
          </>
        )}
      </div>
    </ReportPageLayout>
  );
}
